/** Per-account 5h/7d usage for every connected personal subscription. */
import type {
  CmmdProviderAccountInventoryAccount,
  CmmdProviderUsageSnapshot,
  CmmdProviderUsageSnapshots,
} from "@cmmd-forge/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { cmmdApiClient } from "~/environments/cmmdApi";
import { findProviderAccountByIdentity } from "@cmmd-forge/shared/providerAccountIdentity";

const PROVIDER_USAGE_QUERY_KEY = ["cmmd-provider-usage"] as const;
const REFRESH_INTERVAL_MS = 15 * 60_000;
export type UsageProvider = "codex" | "claude";

interface ProviderUsageRefreshInput {
  readonly provider: UsageProvider;
  readonly providerAccountId: string;
}

export interface ProviderUsageAccountView {
  readonly account: CmmdProviderAccountInventoryAccount;
  readonly snapshot: CmmdProviderUsageSnapshot | null;
}

export interface ProviderUsageView {
  readonly accounts: ReadonlyArray<ProviderUsageAccountView>;
  readonly isPending: boolean;
  readonly error: string | null;
}

/**
 * Why the last refresh failed, as far as the client can tell.
 *
 * `code` is read duck-typed off the rejection rather than through an
 * `instanceof CmmdApiError`, because a card must keep working under a test that
 * mocks the whole `~/environments/cmmdApi` module. A rejection with no string
 * `code` (a network drop, a Codex failure) carries `null` and the card falls
 * back to its generic sentence.
 */
export interface ProviderUsageRefreshFailure {
  readonly code: string | null;
}

export interface ProviderUsageRefreshView {
  readonly refresh: () => void;
  readonly isRefreshing: boolean;
  readonly refreshFailed: boolean;
  readonly refreshFailure: ProviderUsageRefreshFailure | null;
}

function readRefreshFailureCode(error: unknown): string | null {
  const code = (error as { readonly code?: unknown } | null | undefined)?.code;
  return typeof code === "string" ? code : null;
}

function usageProvider(account: CmmdProviderAccountInventoryAccount): UsageProvider {
  return account.provider === "claudeAgent" ? "claude" : "codex";
}

function accountRefreshKey(provider: UsageProvider, providerAccountId: string): string {
  return `${provider}:${providerAccountId}`;
}

export function joinProviderAccountsToUsageSnapshots(
  accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>,
  usage: CmmdProviderUsageSnapshots | undefined,
): ReadonlyArray<ProviderUsageAccountView> {
  const snapshots = usage?.snapshots ?? [];
  return accounts.map((account) => {
    const provider = usageProvider(account);
    const providerAccounts = accounts.filter((candidate) => usageProvider(candidate) === provider);
    const providerSnapshots = snapshots.filter((snapshot) => snapshot.provider === provider);
    const canonicalSnapshot = providerSnapshots.find(
      (snapshot) => snapshot.providerAccountId === account.providerAccountId,
    );
    const legacySnapshot = providerSnapshots.find(
      (snapshot) =>
        findProviderAccountByIdentity(providerAccounts, snapshot.providerAccountId)
          ?.providerAccountId === account.providerAccountId,
    );
    return { account, snapshot: canonicalSnapshot ?? legacySnapshot ?? null };
  });
}

function providerAccountIdentity(
  accounts: ReadonlyArray<CmmdProviderAccountInventoryAccount>,
): string {
  return accounts
    .map((account) =>
      [account.provider, account.providerAccountId, account.needsReconnect, account.isDefault].join(
        ":",
      ),
    )
    .toSorted()
    .join("|");
}

function providerUsageError(input: {
  readonly accountsFailed: boolean;
  readonly initialUsageFailed: boolean;
  readonly cachedUsageUpdateFailed: boolean;
}): string | null {
  if (input.accountsFailed || input.initialUsageFailed) {
    return "CMMD could not report connected-account usage.";
  }
  if (input.cachedUsageUpdateFailed) {
    return "Cached usage could not be updated. Showing the last known values.";
  }
  return null;
}

export function useProviderUsage(): ProviderUsageView {
  const accountsQuery = useQuery({
    queryKey: ["cmmd", "providers", "accounts"],
    queryFn: () => cmmdApiClient.getProviderAccounts(),
    staleTime: 0,
    refetchOnMount: "always",
  });
  const inventory = accountsQuery.data?.accounts ?? [];
  const accountIdentity = providerAccountIdentity(inventory);
  const queryKey = [...PROVIDER_USAGE_QUERY_KEY, accountIdentity] as const;
  const usageQuery = useQuery({
    queryKey,
    queryFn: () => cmmdApiClient.getProviderUsage(),
    enabled: accountsQuery.isSuccess,
    staleTime: REFRESH_INTERVAL_MS,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
    retry: false,
  });
  return {
    accounts: joinProviderAccountsToUsageSnapshots(inventory, usageQuery.data),
    isPending: accountsQuery.isPending || usageQuery.isPending,
    error: providerUsageError({
      accountsFailed: accountsQuery.isError,
      initialUsageFailed: usageQuery.isError && usageQuery.data === undefined,
      cachedUsageUpdateFailed: usageQuery.isRefetchError,
    }),
  };
}

function mergeRefreshedAccount(
  current: CmmdProviderUsageSnapshots | undefined,
  refreshed: CmmdProviderUsageSnapshots,
  input: ProviderUsageRefreshInput,
): CmmdProviderUsageSnapshots {
  if (current === undefined) return refreshed;
  const refreshedAccountKey = accountRefreshKey(input.provider, input.providerAccountId);
  return {
    snapshots: [
      ...current.snapshots.filter(
        (snapshot) =>
          accountRefreshKey(snapshot.provider, snapshot.providerAccountId) !== refreshedAccountKey,
      ),
      ...refreshed.snapshots.filter(
        (snapshot) =>
          accountRefreshKey(snapshot.provider, snapshot.providerAccountId) === refreshedAccountKey,
      ),
    ],
  };
}

/** One independent mutation observer per rendered account card. */
export function useProviderUsageRefresh(
  provider: UsageProvider,
  providerAccountId: string,
): ProviderUsageRefreshView {
  const queryClient = useQueryClient();
  const input = { provider, providerAccountId } as const;
  const refreshMutation = useMutation({
    mutationKey: [...PROVIDER_USAGE_QUERY_KEY, "refresh", provider, providerAccountId],
    mutationFn: () => cmmdApiClient.refreshProviderUsage(provider, providerAccountId),
    onSuccess: (snapshots) =>
      queryClient.setQueriesData<CmmdProviderUsageSnapshots>(
        { queryKey: PROVIDER_USAGE_QUERY_KEY },
        (current) => mergeRefreshedAccount(current, snapshots, input),
      ),
  });

  return {
    refresh: () => refreshMutation.mutate(),
    isRefreshing: refreshMutation.isPending,
    refreshFailed: refreshMutation.isError,
    refreshFailure: refreshMutation.isError
      ? { code: readRefreshFailureCode(refreshMutation.error) }
      : null,
  };
}

/**
 * Read-only usage view for surfaces that must not trigger the refresh side
 * effect (every connected account re-polls the provider). The composer's
 * pre-send gate only needs the last known snapshots; the Usage page owns
 * actual refreshing.
 */
export function useProviderUsageRead(options?: { readonly enabled?: boolean }): {
  readonly accounts: ReadonlyArray<ProviderUsageAccountView>;
  readonly accountsResolved: boolean;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly retry: () => void;
} {
  const accountsQuery = useQuery({
    queryKey: ["cmmd", "providers", "accounts"],
    queryFn: () => cmmdApiClient.getProviderAccounts(),
    staleTime: 60_000,
    retry: false,
    enabled: options?.enabled ?? true,
  });
  const usageQuery = useQuery({
    queryKey: PROVIDER_USAGE_QUERY_KEY,
    queryFn: () => cmmdApiClient.getProviderUsage(),
    enabled: (options?.enabled ?? true) && accountsQuery.isSuccess,
    staleTime: 60_000,
    retry: false,
  });
  return {
    accounts: joinProviderAccountsToUsageSnapshots(
      accountsQuery.data?.accounts ?? [],
      usageQuery.data,
    ),
    accountsResolved: accountsQuery.isSuccess,
    isPending: accountsQuery.isPending || usageQuery.isPending,
    isError: accountsQuery.isError || usageQuery.isError,
    retry: () => {
      void accountsQuery.refetch();
      void usageQuery.refetch();
    },
  };
}

/** Recovery refreshes only after an explicit retry, using provider observations from CMMD. */
export function useProviderUsageRecovery(provider: "codex" | "claudeAgent") {
  const usage = useProviderUsageRead();
  const queryClient = useQueryClient();
  const refresh = useMutation({
    mutationFn: async () => {
      const inventory = await cmmdApiClient.getProviderAccounts();
      queryClient.setQueryData(["cmmd", "providers", "accounts"], inventory);
      const accounts = inventory.accounts.filter(
        (account) => account.provider === provider && !account.needsReconnect,
      );
      const results = await Promise.allSettled(
        accounts.map(async (account) => {
          const input = {
            provider: usageProvider(account),
            providerAccountId: account.providerAccountId,
          };
          const snapshots = await cmmdApiClient.refreshProviderUsage(
            input.provider,
            input.providerAccountId,
          );
          queryClient.setQueriesData<CmmdProviderUsageSnapshots>(
            { queryKey: PROVIDER_USAGE_QUERY_KEY },
            (current) => mergeRefreshedAccount(current, snapshots, input),
          );
        }),
      );
      if (results.length > 0 && results.every((result) => result.status === "rejected"))
        throw new Error("Provider usage refresh failed.");
    },
  });
  return {
    accounts: usage.accounts,
    accountsResolved: usage.accountsResolved,
    isPending: usage.isPending || refresh.isPending,
    isError: usage.isError || refresh.isError,
    retry: () => refresh.mutate(),
  };
}
